import { useQuery } from '@tanstack/react-query';
import { Users2 } from 'lucide-react';
import type { Margin } from '@invest-copilot/shared';
import { fetchJSON as getJSON } from '../lib/api-client';
import { isDelistedError, DELISTED_MESSAGE } from '../lib/error-utils';

/**
 * 融資融券卡片（僅台股顯示）
 * 散戶情緒指標：融資增加=散戶追多（常為反指標），融券增加=空頭情緒升溫
 */
export function MarginCard({ symbol }: { symbol: string }) {
  const isTW = /\.(TW|TWO)$/i.test(symbol);

  const q = useQuery({
    queryKey: ['margin', symbol],
    queryFn: () => getJSON<Margin>(`/api/margin/${encodeURIComponent(symbol)}`),
    enabled: isTW && !!symbol,
    staleTime: 60 * 60_000,
    refetchOnMount: false,
    retry: 2,
    retryDelay: (i) => 500 * (i + 1),
  });

  if (!isTW) return null;
  if (q.isLoading) {
    return (
      <div className="card">
        <div className="text-(--text-muted) text-sm py-4 text-center">載入融資融券…</div>
      </div>
    );
  }
  if (q.error) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Users2 size={16} className="text-(--accent)" />
          <div className="font-bold text-(--text-primary) text-sm">融資融券</div>
        </div>
        <div className={isDelistedError(q.error) ? 'text-(--text-muted) text-xs' : 'text-(--up) text-xs'}>{isDelistedError(q.error) ? DELISTED_MESSAGE : `無法取得：${(q.error as Error).message}`}</div>
      </div>
    );
  }
  if (!q.data || q.data.days.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-2">
          <Users2 size={16} className="text-(--accent)" />
          <div className="font-bold text-(--text-primary) text-sm">融資融券</div>
        </div>
        <div className="text-(--text-muted) text-xs">尚無融資融券資料</div>
      </div>
    );
  }

  const m = q.data;
  const sentiment = sentimentLabel(m.marginNetChange5d, m.shortNetChange5d);

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users2 size={16} className="text-(--accent)" />
          <div>
            <div className="font-bold text-(--text-primary) text-sm">融資融券（散戶情緒）</div>
            <div className="text-[10px] text-(--text-muted)">近 5 日 · 單位：張 · TWSE 收盤後更新</div>
          </div>
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${sentiment.cls}`}>
          {sentiment.text}
        </span>
      </div>

      {/* 5 日累計 stats */}
      <div className="grid grid-cols-4 gap-2 mb-3">
        <SumStat label="融資 5 日" value={m.marginNetChange5d} signFlip />
        <SumStat label="融券 5 日" value={m.shortNetChange5d} />
        <SumStat label="融資使用率" value={`${m.marginUsageRate.toFixed(2)}%`} raw />
        <SumStat label="資券比" value={`${m.marginShortRatio.toFixed(1)}x`} raw />
      </div>

      {/* 每日表格 */}
      <table className="w-full text-[11px] tnum">
        <thead>
          <tr className="text-(--text-muted) text-left border-b border-(--border)">
            <th className="py-0.5 pr-2">日期</th>
            <th className="py-0.5 pr-2 text-right">融資餘額</th>
            <th className="py-0.5 pr-2 text-right">融資 Δ</th>
            <th className="py-0.5 pr-2 text-right">融券餘額</th>
            <th className="py-0.5 pr-2 text-right">融券 Δ</th>
          </tr>
        </thead>
        <tbody>
          {m.days.map((d) => (
            <tr key={d.date} className="border-b border-(--border)/50">
              <td className="py-0.5 pr-2 text-(--text-secondary)">{d.date.slice(5)}</td>
              <td className="py-0.5 pr-2 text-right text-(--text-primary)">{d.marginBalance.toLocaleString()}</td>
              <DayCell value={d.marginNetChange} signFlip />
              <td className="py-0.5 pr-2 text-right text-(--text-primary)">{d.shortBalance.toLocaleString()}</td>
              <DayCell value={d.shortNetChange} />
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-2 text-[10px] text-(--text-muted) leading-relaxed">
        💡 融資增加 + 股價漲 = 散戶追多（常為反指標）；融資減少 + 股價漲 = 健康（散戶賣、法人買）；融券增加 = 空頭情緒升溫。
      </div>
    </div>
  );
}

function SumStat({ label, value, signFlip = false, raw = false }: { label: string; value: number | string; signFlip?: boolean; raw?: boolean }) {
  let cls = 'text-(--text-primary)';
  let display: string;
  if (raw) {
    display = String(value);
  } else {
    const n = value as number;
    // signFlip: 融資減少視為正向（散戶被洗出）
    const positive = signFlip ? n < 0 : n > 0;
    const negative = signFlip ? n > 0 : n < 0;
    cls = positive ? 'text-(--up)' : negative ? 'text-(--down)' : 'text-(--text-primary)';
    display = `${n >= 0 ? '+' : ''}${n.toLocaleString()}`;
  }
  return (
    <div className="p-1.5 rounded bg-(--bg-base) border border-(--border) text-center">
      <div className="text-[9px] text-(--text-muted)">{label}</div>
      <div className={`text-xs font-semibold tnum ${cls}`}>{display}</div>
    </div>
  );
}

function DayCell({ value, signFlip = false }: { value: number; signFlip?: boolean }) {
  const positive = signFlip ? value < 0 : value > 0;
  const negative = signFlip ? value > 0 : value < 0;
  const cls = positive ? 'text-(--up)' : negative ? 'text-(--down)' : 'text-(--text-muted)';
  return (
    <td className={`py-0.5 pr-2 text-right ${cls}`}>
      {value >= 0 ? '+' : ''}{value.toLocaleString()}
    </td>
  );
}

function sentimentLabel(marginNet5d: number, shortNet5d: number): { text: string; cls: string } {
  // 用「融資 + 融券 5 日累計變化」判斷散戶情緒
  // 散戶情緒越熱 → 越接近反指標
  if (marginNet5d > 5000 && shortNet5d < 0) {
    return { text: '🥵 散戶追多 (反指標警示)', cls: 'border-(--down) bg-(--down)/15 text-(--down)' };
  }
  if (marginNet5d > 1000) {
    return { text: '↑ 散戶轉多', cls: 'border-(--warn)/60 bg-(--warn)/10 text-(--warn)' };
  }
  if (marginNet5d < -3000 && shortNet5d > 0) {
    return { text: '🚀 散戶離場 (健康)', cls: 'border-(--up) bg-(--up)/15 text-(--up)' };
  }
  if (marginNet5d < -500) {
    return { text: '↓ 散戶減碼', cls: 'border-(--up)/60 bg-(--up)/10 text-(--up)' };
  }
  if (shortNet5d > 1000) {
    return { text: '🐻 空頭升溫', cls: 'border-(--down)/60 bg-(--down)/10 text-(--down)' };
  }
  return { text: '— 情緒平穩', cls: 'border-(--border) text-(--text-muted)' };
}
